/**
 * Real-robot camera extrinsics -> simulation/render frame.
 *
 * Pure module: no DOM, no Babylon, no Date.now(), no Math.random(). The
 * conversion is unit-tested (tests/unit/camera_frame.test.ts) so the one place
 * a sign error could silently mirror left and right is covered by assertions
 * rather than by eyeballing a screenshot.
 *
 * TWO FRAMES ARE IN PLAY.
 *
 *   Robot frame (config/robot_cameras.json, from the hardware drawing)
 *     origin  centre of the REAR WHEEL AXLE, 142 mm above the ground
 *     +X      forward        +Y  left        +Z  up
 *     units   millimetres, degrees
 *     yaw +   nose swings left        pitch +  nose rises
 *
 *   Sim / Babylon frame (docs/TECHNICAL_ARCHITECTURE.md section 4)
 *     origin  robot BODY CENTRE at GROUND level (robotNode sits at y = 0)
 *     +X      screen-right   +Y  up          +Z  forward (the alley axis)
 *     units   metres, radians
 *     rotation.y +  turns right      rotation.x +  looks down
 *
 * Three conversions therefore happen at once: millimetres to metres, the
 * rear-axle origin to the body-centre-on-the-ground origin, and the lateral
 * axis flip. See LATERAL_SIGN below for the one judgement call involved.
 */

import camerasJson from "../../config/robot_cameras.json";

/** One camera exactly as the hardware team supplied it (mm, degrees). */
export interface CameraExtrinsic {
  id: string;
  name: string;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
}

/** A camera resolved into robot-local render space (metres, radians). */
export interface CameraPose {
  id: string;
  name: string;
  /** Offset from the robot's body centre at ground level, robot-local axes. */
  local: { x: number; y: number; z: number };
  /** Babylon rotation.x — positive looks DOWN. */
  rotX: number;
  /** Babylon rotation.y — positive turns RIGHT. */
  rotY: number;
  /** Babylon rotation.z — roll. Every supplied camera has roll 0. */
  rotZ: number;
  /** Height above the ground plane (m). Convenience for the debug overlay. */
  heightAboveGround: number;
}

/**
 * Lateral handedness of the supplied extrinsics. -1 means "+Y is LEFT", so a
 * positive y maps to a negative (screen-left) render x, and a positive yaw
 * turns left.
 *
 * The note accompanying the extrinsics says "Y-axis to the right", but that
 * reading makes the data contradict itself: `Camera Left` would sit at
 * y = +299.9 on the robot's RIGHT flank and aim 73 deg further right, and
 * `Camera Right` would sit on the left aiming left. Reading +Y as LEFT
 * (ISO 8855 / ROS REP-103, the usual convention for a ground vehicle) is the
 * only reading under which all four horizontal cameras' positions, yaws and
 * names agree with one another.
 *
 * FLIP THIS TO +1 if the hardware team confirms the literal "Y to the right"
 * reading. Nothing else needs to change: the same sign drives both the lateral
 * offset and the yaw direction. Everything downstream — including which side
 * of the alley the participant sees the pedestrian drift toward — follows.
 */
export const LATERAL_SIGN: 1 | -1 = -1;

/**
 * Pitch handedness. `true` means a positive pitch raises the nose.
 *
 * Settled by the robot's own footage, `DATA_OCP/ROS1/*.bag`, which was not
 * available when this was first inferred.
 *
 * The earlier reading was `true`, argued from `Camera Top` (pitch -37): a roof
 * camera covering the near field the body cameras cannot see must look DOWN,
 * so negative had to be down. That put the four body cameras 5-15 deg nose-UP.
 *
 * The recorded front camera says otherwise, and says it plainly. In its
 * frames the horizon sits about 28% of the way down a 640x360 image; under the
 * lens's quoted 51.9 deg vertical equisolid projection that places the optical
 * axis **10-13 deg BELOW** horizontal, against the drawing's 15. Nose-up would
 * put the nearest visible tarmac 3.35 m ahead of the robot; in the real frames
 * the road runs right up under the lens, and the left camera is ground-heavy
 * in the same way. Rendered nose-up, the sky filled three quarters of the
 * frame and the two pictures looked nothing alike.
 *
 * So: positive pitch = nose DOWN, and the body cameras look slightly down at
 * the road, as a 0.65 m lens on a delivery robot should.
 *
 * What this does NOT resolve: `Camera Top` now reads as 37 deg nose-UP, which
 * is a strange thing for a roof camera to be. There is no footage of it in the
 * bags, so it stays as the drawing gives it and is flagged here rather than
 * quietly bent to fit. If a top-camera recording turns up and shows it looking
 * down, that camera needs its own sign, not a flip of this one.
 */
export const PITCH_POSITIVE_IS_UP = false;

const DEG = Math.PI / 180;
const MM = 1 / 1000;

const GEO = camerasJson.geometry_mm;

/** Wheel-axis centreline height above the ground (m) = the frame origin's height. */
export const AXLE_HEIGHT_M = GEO.axle_height * MM;
/** Longitudinal distance from the rear axle to the body centre (m). */
export const BODY_CENTER_FROM_AXLE_M = GEO.body_center_from_axle * MM;
/** Real body extents (m), from the drawing. */
export const BODY_LENGTH_M = GEO.body_length * MM;
export const BODY_WIDTH_M = GEO.body_width * MM;
export const BODY_HEIGHT_M = GEO.body_height * MM;

// ---------------------------------------------------------------------------
// Lens and sensor. Field of view belongs to the LENS, not the camera body:
// the See3CAM_CU21/CU22 are M12 (S-mount) bodies, so the same body yields a
// completely different image depending on what is screwed onto it.

export type ProjectionModel = "equidistant" | "equisolid" | "rectilinear";

/** Shader codes for the projection models; kept next to the model union. */
export const PROJECTION_CODE: Record<ProjectionModel, number> = {
  equidistant: 0,
  equisolid: 1,
  rectilinear: 2,
};

export interface LensProfile {
  /** Profile name as looked up, so anything written out can name its optics. */
  name: string;
  projection: ProjectionModel;
  fov_h_deg: number;
  /** "SOURCED" (from a datasheet) or "UNSPECIFIED" (a stand-in). */
  status: string;
}

/**
 * The camera's sensor and the frame it streams: RouteCAM_CU20, Sony STARVIS
 * IMX462, 1/2.8", 2.9 um, 1937x1097 active. The rendered frame models the
 * FULL HD (1920x1080) streaming mode, which is the mode e-con's quoted field
 * of view refers to.
 */
export const SENSOR = {
  camera: camerasJson.sensor.camera,
  model: camerasJson.sensor.model,
  activeWidthPx: camerasJson.sensor.active_w_px,
  activeHeightPx: camerasJson.sensor.active_h_px,
  widthPx: camerasJson.sensor.stream_w_px,
  heightPx: camerasJson.sensor.stream_h_px,
  /** 16:9 for 1920x1080. The rendered frame keeps this, letterboxed. */
  aspect: camerasJson.sensor.aspect,
} as const;

const LENSES = camerasJson.lenses as Record<
  string,
  { projection: string; fov_h_deg: number; _status: string }
>;

export function lensProfile(name: string): LensProfile {
  const raw = LENSES[name];
  if (!raw) throw new Error(`unknown lens profile: ${name}`);
  return {
    name,
    projection: raw.projection as ProjectionModel,
    fov_h_deg: raw.fov_h_deg,
    status: raw._status,
  };
}

export const LENS_NAMES: readonly string[] = Object.keys(LENSES);

/**
 * Lens fitted by default.
 *
 * The robot is reported to run a fisheye on a CU21/CU22 body, but the lens
 * part number has not been supplied, so this resolves to a profile whose
 * `status` is "UNSPECIFIED". Anything generated from it carries that caveat.
 */
export const DEFAULT_LENS = camerasJson.default_lens;

/**
 * Normalized focal length for a lens, in units where the sensor's half-height
 * is 1.0 and its half-width is the sensor aspect ratio.
 *
 * The lens FOV is quoted horizontally, so the horizontal edge of the sensor
 * (r = halfWidth, theta = fov_h / 2) pins the focal length; everything else
 * follows from the projection model.
 */
export function normalizedFocal(lens: LensProfile, sensorAspect: number): number {
  const halfW = sensorAspect;
  const halfFovH = (lens.fov_h_deg * Math.PI) / 180 / 2;
  switch (lens.projection) {
    case "equidistant":
      return halfW / halfFovH;
    case "equisolid":
      return halfW / (2 * Math.sin(halfFovH / 2));
    case "rectilinear":
      return halfW / Math.tan(halfFovH);
  }
}

/**
 * Largest off-axis angle the lens forms an image for, taken at the sensor
 * corner. Pixels past this are rendered as void rather than invented.
 */
export function maxTheta(lens: LensProfile, sensorAspect: number): number {
  const f = normalizedFocal(lens, sensorAspect);
  const rCorner = Math.hypot(sensorAspect, 1);
  switch (lens.projection) {
    case "equidistant":
      return rCorner / f;
    case "equisolid":
      return 2 * Math.asin(Math.min(1, rCorner / (2 * f)));
    case "rectilinear":
      return Math.atan(rCorner / f);
  }
}

export const CAMERA_EXTRINSICS: readonly CameraExtrinsic[] = camerasJson.cameras;

export type CameraId = "front" | "left" | "right" | "rear" | "top";

export const CAMERA_IDS: readonly CameraId[] = ["front", "left", "right", "rear", "top"];

export function isCameraId(v: string | null): v is CameraId {
  return v !== null && (CAMERA_IDS as readonly string[]).includes(v);
}

/** Resolve one supplied extrinsic into robot-local render space. */
export function cameraPose(cam: CameraExtrinsic): CameraPose {
  const localY = (cam.z + GEO.axle_height) * MM;
  return {
    id: cam.id,
    name: cam.name,
    local: {
      // Lateral: +Y is left, render +X is right (see LATERAL_SIGN).
      x: LATERAL_SIGN * cam.y * MM,
      y: localY,
      // Longitudinal: re-origin from the rear axle to the body centre.
      z: (cam.x - GEO.body_center_from_axle) * MM,
    },
    // Babylon's rotation.x looks down for positive values; supplied pitch
    // rises for positive values, hence the negation.
    rotX: (PITCH_POSITIVE_IS_UP ? -1 : 1) * cam.pitch * DEG,
    // Babylon's rotation.y turns right; supplied yaw turns left under
    // LATERAL_SIGN = -1, so the same sign converts both.
    rotY: LATERAL_SIGN * cam.yaw * DEG,
    rotZ: LATERAL_SIGN * cam.roll * DEG,
    heightAboveGround: localY,
  };
}

/** All five cameras, resolved, in the order the hardware table lists them. */
export function cameraPoses(): CameraPose[] {
  return CAMERA_EXTRINSICS.map(cameraPose);
}

export function cameraPoseById(id: CameraId): CameraPose {
  const cam = CAMERA_EXTRINSICS.find((c) => c.id === id);
  if (!cam) throw new Error(`unknown camera id: ${id}`);
  return cameraPose(cam);
}

export interface RobotPose2D {
  x: number;
  z: number;
  /** Heading from +Z, positive toward +X (docs/TECHNICAL_ARCHITECTURE.md section 4). */
  heading: number;
}

export interface WorldCameraPose {
  position: { x: number; y: number; z: number };
  /** Unit forward vector in world space. */
  forward: { x: number; y: number; z: number };
  /** A point one metre ahead of the camera — ready for `setTarget`. */
  target: { x: number; y: number; z: number };
  /** World yaw (from +Z toward +X) and downward pitch, both radians. */
  worldYaw: number;
  pitchDown: number;
}

/**
 * Place a resolved camera in world space for a given robot pose.
 *
 * The renderer parents its camera to the robot node and lets the engine do
 * this, so this function exists for the headless tests and the debug overlay —
 * but it is the same transform, written once.
 */
export function cameraWorldPose(pose: CameraPose, robot: RobotPose2D): WorldCameraPose {
  const s = Math.sin(robot.heading);
  const c = Math.cos(robot.heading);
  const { x: lx, y: ly, z: lz } = pose.local;

  const position = {
    x: robot.x + lx * c + lz * s,
    y: ly,
    z: robot.z - lx * s + lz * c,
  };

  const worldYaw = robot.heading + pose.rotY;
  const pitchDown = pose.rotX;
  const cp = Math.cos(pitchDown);
  const forward = {
    x: Math.sin(worldYaw) * cp,
    y: -Math.sin(pitchDown),
    z: Math.cos(worldYaw) * cp,
  };

  return {
    position,
    forward,
    target: {
      x: position.x + forward.x,
      y: position.y + forward.y,
      z: position.z + forward.z,
    },
    worldYaw,
    pitchDown,
  };
}
